import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { Composer } from '../jobs/composer.tsx';
import { authClient } from '../lib/auth.ts';

/**
 * `/video-ad` — the composer, ordering something that moves.
 *
 * The same screen as `/static-ad` with a different kind, and a different
 * address so that the thing being made is in the address bar rather than
 * implied. What the kind changes — what the work leaves behind, what it needs
 * before it can be ordered — is the `job_kinds` catalog's answer, so this file
 * only has to name it.
 *
 * `still` is how «animate this one» arrives from the library: the frame
 * already exists, so the order pays for the motion alone.
 */
function VideoAd() {
  const { kind, productId, brandId, still, template } = Route.useSearch();
  const navigate = Route.useNavigate();

  return (
    <Composer
      brandId={brandId}
      entry="blank"
      home={{ to: '/video-ads', label: 'Video ads' }}
      kind={kind}
      onProduct={(id) =>
        void navigate({ search: { kind, brandId, still, template, productId: id } })
      }
      productId={productId}
      stillUploadId={still}
      templateKey={template}
    />
  );
}

export const Route = createFileRoute('/video-ad')({
  validateSearch: z.object({
    /*
     * A `job_kinds` key, defaulting to the one flow that can be ordered here.
     * A string rather than an enum for the reason every catalog reference in
     * this repository is a string: the list is a dictionary and a dictionary
     * that needs a deploy to grow is not one (`docs/CATALOGS.md`). An invented
     * kind is refused by the API, which is where the dictionary actually lives.
     */
    kind: z.string().trim().min(1).default('showcase_video'),
    productId: z.uuid().optional(),
    brandId: z.uuid().optional(),
    still: z.uuid().optional(),
    /**
     * A key of `video_templates`: the ready-made advert this order started
     * from. It arrives from the shelf and fills the brief — a template is a
     * brief somebody else wrote, and nothing else (`routes/shelves.ts`).
     */
    template: z.string().trim().optional(),
  }),
  beforeLoad: async ({ location }) => {
    const { data } = await authClient.getSession();
    if (!data) throw redirect({ to: '/login', search: { redirectTo: location.href } });
  },
  component: VideoAd,
});
